import {
  SingleOfferData,
  MultipleOffersData,
  IncommOfferData,
  NormalizedOfferData,
  BaseOfferConfig,
  IncommBarcodeFormProps,
} from './types'

/**
 * Type guard to check if incomm data contains multiple offers
 */
export const isMultipleOffers = (
  incomm: IncommOfferData,
): incomm is MultipleOffersData => {
  return 'incomm-offers' in incomm && Array.isArray(incomm['incomm-offers'])
}

/**
 * Type guard to check if incomm data is a single offer
 */
export const isSingleOffer = (
  incomm: IncommOfferData,
): incomm is SingleOfferData => {
  return 'offerId' in incomm && !('incomm-offers' in incomm)
}

/**
 * Extract base config fields from any offer data type
 */
export const extractBaseConfig = (incomm: IncommOfferData): BaseOfferConfig => {
  return {
    type: incomm.type,
    title: incomm.title,
    description: incomm.description,
    ctaText: incomm.ctaText,
    supportDescription: incomm.supportDescription,
    legalDescription: incomm.legalDescription,
    skipVerification: incomm.skipVerification,
    testToken: incomm.testToken,
    expirationDate: incomm.expirationDate,
    autoSendEmail: incomm.autoSendEmail,
    autoSendBarcodeEmail: incomm.autoSendBarcodeEmail,
    checkboxes: incomm.checkboxes,
  }
}

/**
 * Convert multiple offers data with single offer to single offer format
 */
const convertSingleOfferFromMultiple = (
  multipleData: MultipleOffersData,
): SingleOfferData => {
  const singleOffer = multipleData['incomm-offers'][0]
  return {
    ...extractBaseConfig(multipleData),
    id: String(singleOffer.offerId),
    offerId: String(singleOffer.offerId),
    offerNumber: singleOffer.offerNumber || '',
    availableStoreIds: singleOffer.availableStoreIds,
    offerBannerImage: singleOffer.offerBannerImage,
  }
}

/**
 * Normalize offer data into a consistent structure
 * Handles both single offer and multiple offers formats
 */
export const normalizeOfferData = (
  incomm: IncommOfferData | undefined,
): NormalizedOfferData | null => {
  if (!incomm) return null

  const config = extractBaseConfig(incomm)

  // Case 1: Legacy single offer format
  if (isSingleOffer(incomm)) {
    return {
      config,
      isMultiple: false,
      singleOffer: incomm,
      offers: [],
      availableStoreIds: incomm.availableStoreIds,
      bannerImageUrl: incomm.offerBannerImage,
    }
  }

  // Case 2: Multiple offers format
  const multipleData = incomm as MultipleOffersData
  const offersArray = multipleData['incomm-offers'] ?? []
  const hasMultipleOffers = offersArray.length > 1

  // If only one offer in array, treat as single offer
  if (!hasMultipleOffers && offersArray.length === 1) {
    const singleOffer = convertSingleOfferFromMultiple(multipleData)
    return {
      config,
      isMultiple: false,
      singleOffer,
      offers: [],
      availableStoreIds: singleOffer.availableStoreIds,
      bannerImageUrl: singleOffer.offerBannerImage,
    }
  }

  // Multiple offers case
  // Ensure all offerIds are strings
  const normalizedOffers = offersArray.map(offer => ({
    ...offer,
    offerId: String(offer.offerId),
  }))
  const allStoreIds = normalizedOffers.flatMap(offer => offer.availableStoreIds)

  return {
    config,
    isMultiple: true,
    singleOffer: null,
    offers: normalizedOffers,
    availableStoreIds: allStoreIds,
    bannerImageUrl: '', // No single banner for multiple offers
  }
}

/**
 * Construct barcode image URL with format parameter
 */
export const getBarcodeImageUrl = (
  barcodeAccessToken: string | undefined,
  barcodeFormat: string | undefined,
): string | null => {
  if (!barcodeAccessToken) return null

  const apiUrl = process.env.DAILYBREAK_API_URL
  if (!apiUrl) return null

  const format = barcodeFormat || 'CODE_128'
  return `${apiUrl}/barcode/image/${barcodeAccessToken}?format=${encodeURIComponent(
    format,
  )}`
}

export const normalizeBooleanFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase()
    return normalizedValue === 'true' || normalizedValue === '1'
  }

  return false
}

/**
 * Create IncommBarcodeFormProps from normalized data
 */
export const createBarcodeFormProps = (
  normalized: NormalizedOfferData,
  options: {
    selectedStoreId: string | null
    formError: any
    onSubmit: (data: any) => Promise<void>
    onStatusModalOpen?: (
      variant: 'already_requested' | 'expired' | 'barcode_limit' | 'clip_limit_exceeded',
    ) => void
  },
): IncommBarcodeFormProps => {
  const { config, availableStoreIds } = normalized
  const { selectedStoreId, formError, onSubmit, onStatusModalOpen } = options

  return {
    ctaLabel: config.ctaText || 'Get Offer',
    selectedStoreId,
    availableStoreIds,
    formError,
    onSubmit,
    onStatusModalOpen,
    skipVerification: config.skipVerification,
    testToken: config.testToken,
    autoSendEmail:
      normalizeBooleanFlag(config.autoSendEmail) ||
      normalizeBooleanFlag(config.autoSendBarcodeEmail),
    checkboxes: config.checkboxes,
  }
}
